import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Filter, CheckCircle, Eye, Trash2, FileCheck, ArrowRight, Play, Edit, ArrowLeft, XCircle, AlertTriangle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { QuestionRenderer, OptionRenderer } from "@/components/QuestionRenderer";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageUpload } from "@/components/ImageUpload";

interface Question {
  id: string;
  grade: number;
  subject: string;
  topic: string;
  questionType: string;
  questionText: string;
  questionImageUrl?: string;
  options: Array<string | { id: string; text: string; imageUrl?: string }>;
  correctAnswer: string;
  explanation: string;
  difficulty: string;
  points: number;
  generationMethod: string;
  status: string;
  reviewNotes?: string;
  createdAt: string;
  groupId?: string;
  groupOrder?: number;
  groupStimulus?: string;
}

export default function QuestionSandbox() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("sandbox");
  const [subjectFilter, setSubjectFilter] = useState<string>("");
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [previewQuestion, setPreviewQuestion] = useState<Question | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedQuestionForReview, setSelectedQuestionForReview] = useState<Question | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [newStatus, setNewStatus] = useState<string>("ready");
  
  // Bulk reject state
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);

  // Edit question state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editForm, setEditForm] = useState<Partial<Question>>({});

  // Memoized onChange handlers for RichTextEditors to prevent infinite loops
  const handleQuestionTextChange = useCallback((value: string) => {
    setEditForm(prev => ({ ...prev, questionText: value }));
  }, []);

  const handleExplanationChange = useCallback((value: string) => {
    setEditForm(prev => ({ ...prev, explanation: value }));
  }, []);

  const handleOptionTextChange = useCallback((index: number, value: string) => {
    setEditForm(prev => {
      const newOptions = [...(prev.options || [])];
      const currentOption = newOptions[index];
      if (currentOption && typeof currentOption === 'object') {
        newOptions[index] = {
          ...currentOption,
          text: value
        };
      }
      return { ...prev, options: newOptions };
    });
  }, []);

  // Fetch grades and subjects from API
  const { data: grades = [] } = useQuery<Array<{id: string; gradeNumber: number; displayName: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/admin/grades'],
  });

  const { data: subjects = [] } = useQuery<Array<{id: string; name: string; displayName: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/admin/subjects'],
  });

  const { data: topics = [] } = useQuery<Array<{id: string; subjectId: string; name: string; displayName: string; isActive: boolean; sortOrder: number}>>({
    queryKey: ['/api/admin/topics'],
  });

  // Filter for active subjects and deduplicate by name only (displayNames vary per grade)
  const activeSubjects = subjects
    .filter(s => s.isActive)
    .reduce((acc, current) => {
      const existsByName = acc.find(item => item.name.toLowerCase() === current.name.toLowerCase());
      if (!existsByName) {
        acc.push(current);
      }
      return acc;
    }, [] as typeof subjects)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Get topics for the selected subject in editForm
  const selectedEditSubject = activeSubjects.find(s => s.name.toLowerCase() === editForm.subject);
  const activeTopicsForEdit = topics
    .filter(t => t.isActive && t.subjectId === selectedEditSubject?.id)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Fetch questions with filters
  const { data: questions = [], isLoading } = useQuery<Question[]>({
    queryKey: ["/api/admin/questions", { status: statusFilter, subject: subjectFilter || undefined }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (subjectFilter && subjectFilter.trim() !== "") {
        params.append("subject", subjectFilter);
      }
      
      const response = await apiRequest("GET", `/api/admin/questions?${params.toString()}`);
      const data = await response.json();
      return data.questions ?? data;
    },
  });

  // Update single question status
  const updateQuestionMutation = useMutation({
    mutationFn: async ({ questionId, status, reviewNotes }: { questionId: string; status: string; reviewNotes?: string }) => {
      const response = await apiRequest("PUT", `/api/admin/questions/${questionId}`, { status, reviewNotes });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/questions"] });
      toast({
        title: "Question updated",
        description: "Question status has been updated successfully",
      });
      setReviewDialogOpen(false);
      setSelectedQuestionForReview(null);
      setReviewNotes("");
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Failed to update question status",
        variant: "destructive",
      });
    },
  });

  // Delete question
  const deleteQuestionMutation = useMutation({
    mutationFn: async (questionId: string) => {
      const response = await apiRequest("DELETE", `/api/admin/questions/${questionId}`);
      return response.json();
    },
    onSuccess: (_, questionId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/questions"] });
      toast({
        title: "Question deleted",
        description: "Question has been deleted successfully",
      });
      setSelectedQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    },
    onError: () => {
      toast({
        title: "Delete failed",
        description: "Failed to delete question",
        variant: "destructive",
      });
    },
  });

  // Bulk update question status
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ questionIds, status, reviewNotes }: { questionIds: string[]; status: string; reviewNotes?: string }) => {
      const response = await apiRequest("PATCH", "/api/admin/questions/bulk-update-status", {
        questionIds,
        status,
        reviewNotes,
      });
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/questions"] });
      toast({
        title: "Bulk update successful",
        description: `${variables.questionIds.length} question(s) updated to ${variables.status} status`,
      });
      setSelectedQuestions(new Set());
    },
    onError: () => {
      toast({
        title: "Bulk update failed",
        description: "Failed to update questions",
        variant: "destructive",
      });
    },
  });

  // Bulk delete (reject) questions
  const bulkDeleteMutation = useMutation({
    mutationFn: async (questionIds: string[]) => {
      const response = await apiRequest("POST", "/api/admin/questions/bulk-delete", { questionIds });
      return response.json();
    },
    onSuccess: (_, questionIds) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/questions"] });
      toast({
        title: "Questions rejected",
        description: `${questionIds.length} question(s) have been removed`,
      });
      setSelectedQuestions(new Set());
      setBulkRejectDialogOpen(false);
    },
    onError: () => {
      toast({
        title: "Rejection failed",
        description: "Failed to reject questions",
        variant: "destructive",
      });
    },
  });

  // Edit question mutation
  const editQuestionMutation = useMutation({
    mutationFn: async (updates: Partial<Question> & { id: string }) => {
      const { id, ...data } = updates;
      const response = await apiRequest("PUT", `/api/admin/questions/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/questions"] });
      toast({
        title: "Question updated",
        description: "Question has been updated successfully",
      });
      setEditDialogOpen(false);
      setEditingQuestion(null);
      setEditForm({});
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Failed to update question",
        variant: "destructive",
      });
    },
  });

  const toggleQuestionSelection = (questionId: string) => {
    const newSelection = new Set(selectedQuestions);
    if (newSelection.has(questionId)) {
      newSelection.delete(questionId);
    } else {
      newSelection.add(questionId);
    }
    setSelectedQuestions(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedQuestions.size === questions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(questions.map(q => q.id)));
    }
  };

  const handleBulkApprove = () => {
    if (selectedQuestions.size === 0) {
      toast({
        title: "No questions selected",
        description: "Please select questions to approve",
        variant: "destructive",
      });
      return;
    }

    bulkUpdateMutation.mutate({
      questionIds: Array.from(selectedQuestions),
      status: statusFilter === "sandbox" ? "ready" : "published",
    });
  };

  const handleBulkReject = () => {
    if (selectedQuestions.size === 0) {
      toast({
        title: "No questions selected",
        description: "Please select questions to reject",
        variant: "destructive",
      });
      return;
    }
    setBulkRejectDialogOpen(true);
  };

  const confirmBulkReject = () => {
    bulkDeleteMutation.mutate(Array.from(selectedQuestions));
  };

  const openReviewDialog = (question: Question) => {
    setSelectedQuestionForReview(question);
    setReviewNotes(question.reviewNotes || "");
    setNewStatus(question.status === "sandbox" ? "ready" : "published");
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = () => {
    if (!selectedQuestionForReview) return;

    updateQuestionMutation.mutate({
      questionId: selectedQuestionForReview.id,
      status: newStatus,
      reviewNotes: reviewNotes.trim() || undefined,
    });
  };

  const generateId = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  const openEditDialog = (question: Question) => {
    setEditingQuestion(question);
    
    // Convert options to QuestionOption format (handle legacy string options)
    const convertedOptions = (question.options || []).map((opt: any, index: number) => {
      if (typeof opt === 'string') {
        return {
          id: generateId(),
          text: opt,
          imageUrl: "",
          math: ""
        };
      }
      // Ensure all options have an id, even if they're already objects
      return {
        ...opt,
        id: opt.id || generateId()
      };
    });
    
    setEditForm({
      grade: question.grade,
      subject: question.subject,
      topic: question.topic,
      questionType: question.questionType,
      questionText: question.questionText,
      questionImageUrl: question.questionImageUrl || "",
      options: convertedOptions,
      correctAnswer: question.correctAnswer,
      explanation: question.explanation,
      difficulty: question.difficulty,
      points: question.points,
      reviewNotes: question.reviewNotes || "",
      groupId: question.groupId || "",
      groupOrder: question.groupOrder || 0,
      groupStimulus: question.groupStimulus || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingQuestion) return;
    
    editQuestionMutation.mutate({
      id: editingQuestion.id,
      ...editForm,
    });
  };

  const moveToSandbox = (questionId: string) => {
    updateQuestionMutation.mutate({
      questionId,
      status: "sandbox",
      reviewNotes: "Moved back to sandbox for review",
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "sandbox":
        return "secondary";
      case "ready":
        return "default";
      case "published":
        return "default";
      default:
        return "secondary";
    }
  };

  const formatOptions = (options: Array<string | { id: string; text: string }>) => {
    return options.map((opt, idx) => {
      const text = typeof opt === "string" ? opt : opt.text;
      return `${String.fromCharCode(65 + idx)}. ${text}`;
    }).join("\n");
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Button
            variant="ghost"
            asChild
            className="mb-2"
            data-testid="button-back-to-admin"
          >
            <Link href="/admin">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Dashboard
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Question Sandbox</h1>
          <p className="text-muted-foreground">
            Review and manage questions before publishing them
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="select-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Select value={subjectFilter || undefined} onValueChange={(value) => setSubjectFilter(value || "")}>
                <SelectTrigger data-testid="select-subject-filter">
                  <SelectValue placeholder="All subjects" />
                </SelectTrigger>
                <SelectContent>
                  {activeSubjects.map((s) => (
                    <SelectItem key={s.id} value={s.name.toLowerCase()}>
                      {s.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setStatusFilter("sandbox");
                  setSubjectFilter("");
                }}
                data-testid="button-clear-filters"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedQuestions.size > 0 && (
        <Card className="border-primary">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <p className="font-medium">
                {selectedQuestions.size} question(s) selected
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={handleBulkApprove}
                  disabled={bulkUpdateMutation.isPending}
                  data-testid="button-bulk-approve"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Selected
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleBulkReject}
                  disabled={bulkDeleteMutation.isPending}
                  data-testid="button-bulk-reject"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Selected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Questions List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Questions ({questions.length})
            </CardTitle>
            {questions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                data-testid="button-select-all"
              >
                {selectedQuestions.size === questions.length ? "Deselect All" : "Select All"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading questions...
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No questions found with the selected filters
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-4">
                {questions.map((question) => (
                  <Card key={question.id} data-testid={`question-card-${question.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedQuestions.has(question.id)}
                            onCheckedChange={() => toggleQuestionSelection(question.id)}
                            data-testid={`checkbox-question-${question.id}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant={getStatusBadgeVariant(question.status)}>
                                {question.status}
                              </Badge>
                              <Badge variant="outline">Grade {question.grade}</Badge>
                              <Badge variant="outline">{question.subject}</Badge>
                              <Badge variant="outline">{question.difficulty}</Badge>
                              {question.generationMethod !== "manual" && (
                                <Badge variant="secondary">{question.generationMethod}</Badge>
                              )}
                            </div>
                            <p className="font-medium mb-2">{question.questionText}</p>
                            <p className="text-sm text-muted-foreground">
                              Topic: {question.topic} | Points: {question.points}
                            </p>
                            {question.reviewNotes && (
                              <div className="mt-2 p-2 bg-muted rounded-md">
                                <p className="text-sm font-medium">Review Notes:</p>
                                <p className="text-sm text-muted-foreground">{question.reviewNotes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(question)}
                            data-testid={`button-edit-${question.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPreviewQuestion(question)}
                            data-testid={`button-preview-${question.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {question.status === "published" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => moveToSandbox(question.id)}
                              data-testid={`button-move-sandbox-${question.id}`}
                            >
                              <ArrowRight className="h-4 w-4 rotate-180" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReviewDialog(question)}
                            data-testid={`button-review-${question.id}`}
                          >
                            <FileCheck className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Are you sure you want to delete this question? This action cannot be undone.`)) {
                                deleteQuestionMutation.mutate(question.id);
                              }
                            }}
                            disabled={deleteQuestionMutation.isPending}
                            data-testid={`button-delete-${question.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewQuestion} onOpenChange={() => setPreviewQuestion(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Question Preview</DialogTitle>
            <DialogDescription>
              Preview the question as students will see it
            </DialogDescription>
          </DialogHeader>
          {previewQuestion && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Badge variant="outline">Grade {previewQuestion.grade}</Badge>
                <Badge variant="outline">{previewQuestion.subject}</Badge>
                <Badge variant="outline">{previewQuestion.topic}</Badge>
                <Badge variant="outline">{previewQuestion.difficulty}</Badge>
              </div>

              <QuestionRenderer
                text={previewQuestion.questionText}
                imageUrl={previewQuestion.questionImageUrl}
              />

              <div>
                <Label className="text-base font-semibold mb-3 block">Options:</Label>
                <div className="space-y-3">
                  {previewQuestion.options.map((opt, idx) => {
                    const optionText = typeof opt === "string" ? opt : opt.text;
                    const optionId = typeof opt === "string" ? opt : opt.id;
                    const optionImageUrl = typeof opt === "object" && opt.imageUrl ? opt.imageUrl : undefined;
                    const isCorrect = optionId === previewQuestion.correctAnswer || optionText === previewQuestion.correctAnswer;
                    const optionLetter = String.fromCharCode(65 + idx);

                    return (
                      <div
                        key={idx}
                        className={`p-3 rounded-md border transition-colors ${
                          isCorrect 
                            ? "border-green-500 bg-green-50 dark:bg-green-950/30" 
                            : "border-border bg-card"
                        }`}
                      >
                        <OptionRenderer
                          text={optionText}
                          imageUrl={optionImageUrl}
                          optionLetter={optionLetter}
                        />
                        {isCorrect && (
                          <span className="ml-8 text-sm text-green-600 dark:text-green-400 font-medium">
                            ✓ Correct Answer
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold mb-2 block">Explanation:</Label>
                <QuestionRenderer text={previewQuestion.explanation} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Question</DialogTitle>
            <DialogDescription>
              Update the status and add review notes for this question
            </DialogDescription>
          </DialogHeader>
          {selectedQuestionForReview && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Question:</p>
                <p className="font-medium">{selectedQuestionForReview.questionText}</p>
              </div>
              <div>
                <Label>New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="select-new-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Review Notes (Optional)</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add feedback or notes about this question..."
                  rows={4}
                  data-testid="textarea-review-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewDialogOpen(false)}
              data-testid="button-cancel-review"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReviewSubmit}
              disabled={updateQuestionMutation.isPending}
              data-testid="button-submit-review"
            >
              Update Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Question</DialogTitle>
            <DialogDescription>
              Make changes to the question content and metadata
            </DialogDescription>
          </DialogHeader>
          {editingQuestion && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Grade</Label>
                  <Select
                    value={editForm.grade?.toString()}
                    onValueChange={(value) => setEditForm({ ...editForm, grade: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="select-edit-grade">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">Grade 4</SelectItem>
                      <SelectItem value="5">Grade 5</SelectItem>
                      <SelectItem value="6">Grade 6</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Select
                    value={editForm.subject}
                    onValueChange={(value) => setEditForm({ ...editForm, subject: value })}
                  >
                    <SelectTrigger data-testid="select-edit-subject">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activeSubjects.map((s) => (
                        <SelectItem key={s.id} value={s.name.toLowerCase()}>
                          {s.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Topic</Label>
                  <Select
                    value={editForm.topic}
                    onValueChange={(value) => setEditForm({ ...editForm, topic: value })}
                  >
                    <SelectTrigger data-testid="select-edit-topic">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTopicsForEdit.map((t) => (
                        <SelectItem key={t.id} value={t.name.toLowerCase()}>
                          {t.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Question Text</Label>
                <RichTextEditor
                  value={editForm.questionText || ""}
                  onChange={handleQuestionTextChange}
                  placeholder="Enter the question text..."
                  height={200}
                />
              </div>

              <ImageUpload
                currentImageUrl={editForm.questionImageUrl}
                onImageUpload={(imageUrl) => setEditForm({ ...editForm, questionImageUrl: imageUrl })}
                onImageRemove={() => setEditForm({ ...editForm, questionImageUrl: "" })}
                label="Question Image (Optional)"
              />

              {/* Multiple Choice Options */}
              {editForm.questionType === "multiple-choice" && (
                <>
                  <div className="space-y-3">
                    <Label>Answer Options</Label>
                    {Array.isArray(editForm.options) && editForm.options.map((option: any, index: number) => {
                      const optionLetter = String.fromCharCode(65 + index);
                      const optionText = option.text || "";
                      
                      return (
                        <div key={option.id || `option-${index}`} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="w-8 h-8 flex items-center justify-center p-0 flex-shrink-0">
                              {optionLetter}
                            </Badge>
                            <span className="text-sm text-muted-foreground">Option {optionLetter}</span>
                          </div>
                          <RichTextEditor
                            value={optionText}
                            onChange={(value) => handleOptionTextChange(index, value)}
                            placeholder={`Enter option ${optionLetter}`}
                            height={120}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-2">
                    <Label>Correct Answer</Label>
                    <Select
                      value={editForm.correctAnswer}
                      onValueChange={(value) => setEditForm({ ...editForm, correctAnswer: value })}
                    >
                      <SelectTrigger data-testid="select-edit-correct-answer">
                        <SelectValue placeholder="Select the correct answer" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.isArray(editForm.options) && editForm.options.map((option: any, index: number) => {
                          const letter = String.fromCharCode(65 + index);
                          const displayText = option.text || "(empty)";
                          return (
                            <SelectItem key={option.id || `select-option-${index}`} value={option.id}>
                              {letter}. {displayText}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Explanation</Label>
                <RichTextEditor
                  value={editForm.explanation || ""}
                  onChange={handleExplanationChange}
                  placeholder="Explain why this is the correct answer..."
                  height={150}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <Select 
                    value={editForm.difficulty || "medium"} 
                    onValueChange={(value) => setEditForm({ ...editForm, difficulty: value })}
                  >
                    <SelectTrigger data-testid="select-edit-difficulty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Points</Label>
                  <Input
                    type="number"
                    value={editForm.points || 100}
                    onChange={(e) => setEditForm({ ...editForm, points: parseInt(e.target.value) || 100 })}
                    data-testid="input-edit-points"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Review Notes</Label>
                <Textarea
                  value={editForm.reviewNotes || ""}
                  onChange={(e) => setEditForm({ ...editForm, reviewNotes: e.target.value })}
                  placeholder="Add notes about this question..."
                  rows={3}
                  data-testid="textarea-edit-notes"
                />
              </div>

              <div className="space-y-4 border-t pt-4">
                <Label className="text-base font-semibold">Question Group (Multi-Part)</Label>
                <div className="space-y-2">
                  <Label>Group ID</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={editForm.groupId || ""}
                      onChange={(e) => setEditForm({ ...editForm, groupId: e.target.value })}
                      placeholder="Enter group ID or generate one"
                      data-testid="input-edit-group-id"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditForm({ ...editForm, groupId: crypto.randomUUID() })}
                    >
                      Generate
                    </Button>
                    {editForm.groupId && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditForm({ ...editForm, groupId: "", groupOrder: 0, groupStimulus: "" })}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {editForm.groupId && (
                  <>
                    <div className="space-y-2">
                      <Label>Group Order (0 = Part A, 1 = Part B, etc.)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={editForm.groupOrder || 0}
                        onChange={(e) => setEditForm({ ...editForm, groupOrder: parseInt(e.target.value) || 0 })}
                        data-testid="input-edit-group-order"
                      />
                    </div>

                    {(editForm.groupOrder === 0 || editForm.groupOrder === undefined) && (
                      <div className="space-y-2">
                        <Label>Group Stimulus (Shared passage/context for all parts)</Label>
                        <Textarea
                          value={editForm.groupStimulus || ""}
                          onChange={(e) => setEditForm({ ...editForm, groupStimulus: e.target.value })}
                          placeholder="Enter the shared reading passage or scenario that applies to all parts in this question group..."
                          rows={5}
                          data-testid="textarea-edit-group-stimulus"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                  data-testid="button-cancel-edit"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={editQuestionMutation.isPending}
                  data-testid="button-submit-edit"
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Reject Confirmation Dialog */}
      <Dialog open={bulkRejectDialogOpen} onOpenChange={setBulkRejectDialogOpen}>
        <DialogContent className="max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Reject {selectedQuestions.size} Question{selectedQuestions.size !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to reject and remove {selectedQuestions.size} selected question{selectedQuestions.size !== 1 ? "s" : ""}? This will permanently remove them from the question bank.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 py-2">
            <div className="rounded-md bg-destructive/10 p-4 space-y-2">
              <p className="text-sm font-medium">Questions to be rejected:</p>
              <ScrollArea className="h-[200px]">
                <ul className="text-sm text-muted-foreground space-y-1 pr-4">
                  {questions
                    .filter(q => selectedQuestions.has(q.id))
                    .map(q => (
                      <li key={q.id} className="flex items-center gap-2">
                        <XCircle className="h-3 w-3 text-destructive shrink-0" />
                        <span className="truncate">{q.questionText}</span>
                      </li>
                    ))}
                </ul>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter className="gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => setBulkRejectDialogOpen(false)}
              disabled={bulkDeleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBulkReject}
              disabled={bulkDeleteMutation.isPending}
              data-testid="button-confirm-bulk-reject"
            >
              {bulkDeleteMutation.isPending ? "Rejecting..." : `Reject ${selectedQuestions.size} Question${selectedQuestions.size !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
